import type { Ref } from 'vue'
import type { SearchSuggestion } from '~/types'

function normalizeForUrl(text: string): string {
  return text.toLowerCase().replace(/[\s/&.,+-]+/g, '-')
}

function filterStatic(items: SearchSuggestion[], query: string): SearchSuggestion[] {
  if (!query) return []
  const lowerQuery = query.toLowerCase()
  return items.filter(item =>
    item.label?.toLowerCase().includes(lowerQuery)
    || (lowerQuery.length > 3 && item.value?.toLowerCase().includes(lowerQuery)),
  )
}

export function useSearchSuggestions(searchQuery: Ref<string>) {
  const searchStore = useSearchStore()
  const supplierStore = useSupplier()
  const categoryStore = useCategoryGroup()
  const { t } = useI18n()
  const isLoading = computed(() => searchStore.isLoading)

  watch(() => categoryStore.categoryGroup, (groups) => {
    if (groups.length > 0) {
      categoryStore.fetchAllCategories()
    }
  }, { immediate: true })

  // Suggestions Logic
  const staticSuggestions = computed<SearchSuggestion[]>(() => {
    const suppliers: SearchSuggestion[] = supplierStore.suppliers.map(s => ({
      label: s.name,
      value: s.handle,
      type: 'supplier',
      url: `/supplier/${s.handle}`,
      imageUrl: s.imageUrl ?? s.image,
    }))

    const categories: SearchSuggestion[] = categoryStore.categoryGroup.flatMap((group) => {
      const children = categoryStore.allCategoriesByGroup[group.handle] ?? []
      const parentUrl = normalizeForUrl(t(group.i18nKey))

      return children
        .filter(cat => !cat.i18nKey.startsWith('enum.') || t(cat.i18nKey) !== cat.i18nKey)
        .map(cat => ({
          label: t(cat.i18nKey),
          value: cat.handle,
          type: 'category',
          url: `/${parentUrl}/${normalizeForUrl(t(cat.i18nKey))}`,
        }))
    })

    return [...suppliers, ...categories]
  })

  // dynamic suggestions based on search query
  const suggestionItems = computed<SearchSuggestion[]>(() => {
    const products: SearchSuggestion[] = (searchStore.data?.suggestions || []).map(s => ({
      label: s.label,
      value: s.value,
      type: 'product',
    }))

    if (!searchQuery.value) return products

    const filteredStatic = filterStatic(staticSuggestions.value, searchQuery.value)
    return [...filteredStatic, ...products]
  })

  function resetSearchSuggestions() {
    searchStore.resetToHistory()
  }

  // Search Logic for autocomplete by delay 300ms
  const handleSearch = useDebounceFn((query: string) => {
    if (!query) {
      searchStore.resetToHistory()
      return
    }
    searchStore.autocomplete(query)
  }, 300)

  watch(searchQuery, q => handleSearch(q))

  return {
    suggestionItems,
    resetSearchSuggestions,
    isLoading,
  }
}
